// Spuit (eyedropper) — Classic UI 後追い実装 (Phase 6.7 サブ task、consult 2026-05-06).
//
// 旧版 `OekakiChatJava/OekakiChatJavaApplet/src/oekakichat/applet/WhiteboardCanvas.java:1235`
// (右クリック/Alt+クリックで `paintToolCanvas.setPaletteColor(col)` 呼び出し)
// を新版に移植したもの。WS protocol への影響はゼロ — 完全クライアントローカル
// (`docs/_wip/protocol-events.md:499` で「太さ・色・スポイト = クライアント
// ローカル」と確定済).
//
// ## 動作仕様 (consult 2026-05-06 で確定)
//
//   - **トリガー**: `contextmenu` (= 右クリック) **OR** `pointerdown` で
//     `e.altKey === true` (= Alt+クリック). 両方サポート.
//   - **取得色**: 選択中 layer の **合成前ピクセル色** (= MVP は layer 0,
//     β は active layer。`engine.getLayerPixelColor(layerId, x, y)`).
//   - **保存先**: 描画色 (palette) のみ. 現在の alpha は維持. マスク色は
//     触らない (= 旧版踏襲、マスク色は別 UI の独立 slot).
//   - **キャンバス外クリック**: 何もしない (`getLayerPixelColor` が null
//     を返したら no-op).
//   - **alpha は無視**: 取得色の alpha を採用しない (旧版 `new Color(col)`
//     が alpha bit を捨てるのと整合).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM**
// (`packages/web/vitest.config.ts:9`). React component や PointerEvent を
// node 環境で素直に組み立てられないため、spuit のロジック (= Alt 判定 +
// pixel pick + RGB pack) を純粋関数に切り出して単体テスト可能にする.
// `DrawingCanvas.tsx` 側はこれらを組み合わせるだけで自前のテストは不要.

/**
 * Engine に求める最小インタフェース (= mock しやすい).
 *
 * PR 2-β (2026-05-17): worker offload で `getLayerPixelColor` は
 * `Promise<...>` 返却に変更. 内部実装 (`CanvasDrawingEngine`) は inline
 * path でも `Promise.resolve` で wrap して 1 つの非同期 shape を提供する.
 * spuit caller (`pickColorFromEngine`) も async になり、`DrawingCanvas`
 * の pointerdown / pointermove / contextmenu handler 側で `await` する.
 *
 * 体感: inline path は実質 micro-task 1 つ、worker path は ~1 frame の
 * round-trip. spuit クリック / drag のフィードバックには十分高速 (= 人間
 * の知覚閾値以下).
 */
/**
 * スポイトのサンプル元モード (spuit-sample-modes-2026-06-22 §2)。
 * `@oekaki/drawing-core` の `SpuitMode` と同じ enum 値を持つ (= engine API へ
 * そのまま渡せる)。型の single source of truth は engine 側だが、`spuit.ts` は
 * 描画エンジン本体に直接依存させたくない (= pure helper、node env でテスト) ため、
 * ここで構造的に同型の文字列 union を再宣言する。値が増減したら両方を揃える
 * (= `samplePixel` の引数互換は構造的に保たれる)。
 *
 *   - `'display'`   (A, 既定) — 全可視レイヤー + bg (見たまま)。RGB のみ。
 *   - `'layer'`     (B)       — 選択レイヤーの生ピクセル、bg 無視。**α を扱う**。
 *   - `'layer-bg'`  (C)       — 選択レイヤー + bg (= 旧 getLayerPixelColor 相当)。RGB のみ。
 */
export type SpuitSampleMode = 'display' | 'layer' | 'layer-bg';

export interface SpuitEngineLike {
  /**
   * 1px を選択モードでサンプルする (spuit-sample-modes-2026-06-22)。
   * 戻り値の `a` の意味はモード依存:
   *   - A (`'display'`) / C (`'layer-bg'`): `a` は 255 固定 (無意味、RGB のみ採用)。
   *   - B (`'layer'`): 選択レイヤーの **実 α**。handler が `a === 0` を消しゴム切替に
   *     使い、1..255 は RGB のみ採用する (ペン alpha は変えない)。
   * OOB / unknown layer / disposed engine → `null` (no-op)。
   */
  samplePixel(
    mode: SpuitSampleMode,
    layerId: number,
    x: number,
    y: number,
  ): Promise<{ r: number; g: number; b: number; a: number } | null>;
}

/** PointerEvent / MouseEvent から拾う最小フィールド. test mock しやすい形. */
export interface SpuitPointerLike {
  readonly altKey: boolean;
  /**
   * `MouseEvent.button` (0=primary, 2=secondary). `pointerdown` 経由で来る
   * Alt+左クリックは `button === 0`、右クリックは `contextmenu` 経路で別
   * ハンドラが拾う想定. Alt+右クリックでも spuit 発動して問題ない.
   */
  readonly button: number;
}

/**
 * Alt 修飾子付きクリックでスポイト発動とみなすかの判定.
 *
 * - `altKey === true` で発動. button は問わない (Alt+左, Alt+中ボタン
 *   どちらでも OK. ブラウザ実装上 Alt+右も Alt 修飾があれば発動).
 * - 修飾なしクリックは通常の描画 (= スポイト発動 false → caller は通常の
 *   stroke 開始フローへ).
 * - **右クリック単体 (`button === 2`, altKey なし) はここでは false** —
 *   ブラウザの `contextmenu` イベント側で spuit を発動する役割分担に
 *   なっている. ただし pointerdown は contextmenu より先に fire するため,
 *   caller (= `DrawingCanvas`) は `shouldSkipStrokeOnPointerDown` を別途
 *   見て **stroke 開始だけは skip** する必要がある (= 右クリックでも線が
 *   引かれてしまうバグの修正、2026-05-06).
 *
 * `contextmenu` (右クリック) 経路はこの関数を経由せず常に発動する仕様
 * (右クリック = スポイト固定) — caller 側で経路ごとにこの関数を呼ぶか
 * 直接 `pickColorFromEngine` を呼ぶかを使い分ける.
 */
export const shouldTriggerSpuitFromPointerDown = (e: SpuitPointerLike): boolean => {
  return e.altKey === true;
};

/**
 * pointerdown で **初回ピックを撃つべきか** の判定 (2026-06-20、`fix/spuit-pen-rightclick`).
 *
 * `altKey === true` (= Alt+クリック) **OR** `button === 2` (= 右クリック / ペンの
 * バレル右ボタン) で true.
 *
 * ## なぜ右クリックも pointerdown で拾うのか (= contextmenu 依存をやめる)
 *
 * 旧実装は右クリックの初回ピックを **`contextmenu` イベント任せ**にしていた
 * (pointerdown は `shouldSkipStrokeOnPointerDown` で stroke 開始を止めて drag モードに
 * 入るだけ)。マウスは右クリックで `contextmenu` を必ず出すので効いたが、**ペンの
 * バレル右クリックは Windows Ink 下で `contextmenu` を安定発火しない**ため初回ピックが
 * 来ず「一瞬だけ効く」状態になっていた (ユーザ報告: Wacom Bamboo + Windows Ink)。
 *
 * 旧 Java 版 (AWT) は `mousePressed(BUTTON3)` で押下即 `setPaletteColor()` していた
 * (= 押下時点でピック)。本 helper はその挙動へ戻すもの: pointerdown の時点で右クリックも
 * 即ピックし、`contextmenu` への依存をなくす。`contextmenu` ハンドラは引き続き
 * `preventDefault` (ブラウザメニュー抑止) は行うが、**初回ピックは pointerdown で済む**
 * ため二重ピックにならないよう caller 側でガードする。
 *
 * `shouldSkipStrokeOnPointerDown` / `shouldEnterSpuitDragMode` と同じ条件 (= スポイト
 * ジェスチャの superset)。3 者が揃っているのは意図的 (= 「右クリック / Alt は stroke を
 * 開始せず、pointerdown で即ピックし、ドラッグ中も連続ピックする」を一貫させる)。
 */
export const shouldPickOnPointerDown = (e: SpuitPointerLike): boolean => {
  return e.altKey === true || e.button === 2;
};

/**
 * pointerdown を受け取ったとき **通常の stroke 開始を skip すべきか** の判定.
 *
 * spuit 発動条件の superset:
 *   - `altKey === true` (= Alt+クリック、ここで spuit も発動する)
 *   - `button === 2` (= 右クリック. spuit 自体は後続の `contextmenu` で
 *     発動するが, pointerdown は contextmenu より先に fire するため
 *     ここで stroke 開始を止めないと「右クリックで線が引かれる」バグになる).
 *
 * 言い換えると "この pointerdown はスポイトジェスチャの一部だから描画は
 * 開始しない" を表す. 実発火は `shouldTriggerSpuitFromPointerDown` と
 * contextmenu ハンドラで分担する.
 */
export const shouldSkipStrokeOnPointerDown = (e: SpuitPointerLike): boolean => {
  return e.altKey === true || e.button === 2;
};

/**
 * spuit ドラッグモード ON/OFF 判定ユーティリティ (2026-05-06 追加 — Photoshop
 * 等の eyedropper 踏襲で「ドラッグ中も連続スポイト」する).
 *
 * spuit モードに入る条件 = `shouldSkipStrokeOnPointerDown` と同じ
 * (Alt+クリック OR 右クリック). ドラッグ中の pointermove はこのフラグを
 * 見て連続スポイトする.
 *
 * ## なぜ「Alt キーを途中で離しても継続」なのか
 *
 * Photoshop / Krita / Procreate 等の主要グラフィックツールは pointerdown 時
 * の修飾キー状態で確定し、ドラッグ中の修飾キー解放を無視する. ユーザが
 * Alt を押しっぱなしのまま広範囲を拾うのは指がつらく、ドラッグ開始後は
 * Alt を離せた方が UX が良い. ここでも同じ仕様を採用.
 *
 * → ドラッグ中の pointermove では altKey は **見ない**. pointerdown で
 *   set した spuit active フラグだけを見る.
 *
 * 実装側 (= `DrawingCanvas`) は `useRef<boolean>` を持ち:
 *   - pointerdown で `shouldEnterSpuitDragMode(e)` が true なら ref = true
 *   - pointermove で ref が true なら連続スポイト + stroke 処理を skip
 *   - pointerup / pointercancel / pointerleave で ref = false
 */
export const shouldEnterSpuitDragMode = (e: SpuitPointerLike): boolean => {
  return shouldSkipStrokeOnPointerDown(e);
};

/**
 * RGB 24bit packing (`(r<<16) | (g<<8) | b`). `PenConfig.color` がこの形式
 * を期待しているのと整合 (`packages/web/src/domain/PenConfig.ts:14`).
 */
export const packRgb24 = (rgb: { r: number; g: number; b: number }): number => {
  // & 0xff でガード — engine は 0..255 を返すはずだが防御的に.
  const r = rgb.r & 0xff;
  const g = rgb.g & 0xff;
  const b = rgb.b & 0xff;
  return (r << 16) | (g << 8) | b;
};

/**
 * 1 回の spuit ピックの結果 (spuit-sample-modes-2026-06-22)。
 *
 *   - `{ kind: 'color', rgb24 }` … パレット色を `rgb24` に更新する (= 従来挙動)。
 *   - `{ kind: 'eraser' }` … モード B で **α0 (= 完全透明)** を拾ったケース。色は
 *     変えず、ツールを消しゴムへ切り替える (設計書 §2 B 行)。
 */
export type SpuitPickResult = { kind: 'color'; rgb24: number } | { kind: 'eraser' };

/**
 * Engine から選択モードで 1px をサンプルし、spuit の適用結果を返す。
 * OOB / unknown layer / disposed engine では null (= caller は no-op)。
 *
 * モード別 (設計書 §2):
 *   - A (`'display'`) / C (`'layer-bg'`): RGB だけを採用 (`a` は 255 固定で無意味)。
 *     → 常に `{ kind: 'color' }`。
 *   - B (`'layer'`): 選択レイヤーの実 α を見る。
 *     - `a === 0` (完全透明) → `{ kind: 'eraser' }` (色は変えずツール切替)。
 *     - `a` 1..255 → `{ kind: 'color' }` (RGB のみ採用、ペン alpha は触らない)。
 *
 * Caller (= `DrawingCanvas`) は null のとき何もせず return することを期待する
 * (= キャンバス余白クリックで誤って色が変わる事故を防ぐ)。
 *
 * PR 2-β (2026-05-17): worker offload で async 化. caller は `void
 * pickColorFromEngine(...).then(...)` か `await` で消費する.
 */
export const pickColorFromEngine = async (
  engine: SpuitEngineLike,
  mode: SpuitSampleMode,
  layerId: number,
  x: number,
  y: number,
): Promise<SpuitPickResult | null> => {
  const px = await engine.samplePixel(mode, layerId, x, y);
  if (px === null) return null;
  // モード B のみ α0 を消しゴム切替に使う。A/C は `a` 固定 255 なのでこの分岐は通らない。
  if (mode === 'layer' && px.a === 0) return { kind: 'eraser' };
  return { kind: 'color', rgb24: packRgb24(px) };
};

// ────────────────────────────────────────────────────────────────────────────
// スポイトドラッグの色追従コアレッシング (2026-06-13、feature/spuit-drag-realtime).
//
// ## 直していること
//
// 旧実装 (`DrawingCanvas.tsx` の `tryHandleSpuit`) はドラッグ中の pointermove ごとに
// `pickColorFromEngine` を **fire-and-forget** で叩いていた。worker offload 有効時は
// 各 pick が ~1 frame の worker 往復になり:
//   1. 速いドラッグで pick が溜まる (毎 move で新規呼び出しが積む)
//   2. worker 往復は順序保証が無く、古い位置の色が新しい位置の色の **後** に resolve
//      して表示色を上書きする (= 順不同レース)。
// → 表示色がカーソルに追従しない。確定色も「最後に *解決* した色」なので同じ影響を受ける。
//
// ## 直し方 (engine / protocol / worker は無改修)
//
// 1. **in-flight を常に最大1つに絞る (coalescing)**: ドラッグ中の `request()` は「最新の
//    pick 対象 (layerId, x, y)」を ref に上書きするだけ。pick は同時に1つだけ走り、resolve
//    したときに「その間に対象が更新されていれば、最新位置で次の1回を撃つ」。worker
//    スループットに自然にペース合わせされ、常に現在位置へ収束する。
// 2. **seq (世代) ガード**: 各 in-flight pick に単調増加の連番を振り、resolve 時に「自分が
//    現在実行中の世代か」を確認して、最新世代の結果以外は `onColor`/確定色に使わない。
//    順不同上書きを排除する (上の coalescing で同時1つに絞っているので「自分が最新か」は
//    `seq === inFlightSeq` で判定できる)。
// 3. **確定色 (`lastColor`) は「最後に *リクエストした* 位置の色」**になる。最新世代の
//    pick が resolve したときだけ更新するので、ドラッグ末尾でリクエストした位置の色が
//    最終的に残る。`commit()` はその色を1回流す (= 最近色には離した時点でカーソル下に
//    あった色が入る)。pick が未解決のまま commit が来たら pending を立て、最新世代の
//    resolve 時に発火する (= 取りこぼし防止)。
//
// 単発クリック (Alt+クリック / 右クリック / 持続モードの素クリック) も同じ `request()` を
// 通る: in-flight が無い状態から1回 pick が走り、その間に対象が更新されなければ再 pick も
// 起きず、従来どおり1回 pick で色が入る (= 挙動不変)。

/**
 * 1 つの pick 対象 (engine 座標系、flip 補正適用後)。
 *
 * `mode` は spuit-sample-modes-2026-06-22 で追加。request ごとに現在の選択モードを
 * 運ぶ (= ドラッグ中にモードが変わっても最新リクエストの mode で pick される)。
 */
interface SpuitTarget {
  readonly mode: SpuitSampleMode;
  readonly layerId: number;
  readonly x: number;
  readonly y: number;
}

export interface SpuitCoalescerOptions {
  /** 最新世代の pick が色を取れたとき (毎回) 呼ぶ。pen.color へ反映する想定。 */
  readonly onColor: (rgb24: number) => void;
  /**
   * 操作の確定 (= 1 ドラッグの release) で「最近色に残す確定の1色」を1回だけ通知する。
   * 未指定なら確定通知を行わない (= Classic 等、最近色機能を持たない caller)。
   */
  readonly onCommit?: (rgb24: number) => void;
  /**
   * モード B で α0 (= 完全透明) を拾ったとき呼ぶ (spuit-sample-modes-2026-06-22 §2 B)。
   * パレット色は変えず、ツールを消しゴムへ切り替える想定。最新世代の pick でのみ発火する
   * (= seq ガード対象)。未指定なら no-op (= モード B を使わない caller との後方互換)。
   *
   * `onColor` と排他: 1 回の pick 結果は「色」か「消しゴム」のどちらか一方。
   */
  readonly onEraser?: () => void;
}

/**
 * スポイトの pick を coalescing + seq ガードで捌くステートマシン。
 *
 * DOM を一切触らないので node env の vitest で deferred mock を使ってレースを再現テスト
 * できる (= `pickColorFromEngine` のラッパとして engine を受け取り、内部で `getLayerPixelColor`
 * の resolve 順を入れ替えられる)。`DrawingCanvas` 側はこのインスタンスを1個 hold し、
 * pointerdown / pointermove で `request()`、release で `commit()`、操作開始で `begin()` を呼ぶ。
 *
 * ## ライフサイクル
 *   - `begin()`  … 1 スポイト操作の開始。確定状態 (lastColor / pending) をリセット。
 *   - `request(target)` … pointerdown / pointermove。最新対象を覚えて pick をキック
 *                         (in-flight があれば対象更新だけ → resolve 時に最新で再 pick)。
 *   - `commit()` … release (pointerup / cancel / leave)。確定の1色を1回通知 (or pending)。
 */
export class SpuitPickCoalescer {
  /**
   * 紐づく engine。caller は engine が差し替わったかを `coalescer.engine === engine` で
   * 判定し、変わっていれば作り直す (= 純 state machine なので dispose 不要、GC 任せ)。
   */
  readonly engine: SpuitEngineLike;
  private readonly opts: SpuitCoalescerOptions;
  /** 直近に request された pick 対象 (まだ pick していない / pick 中に上書きされうる)。 */
  private latest: SpuitTarget | null = null;
  /** in-flight pick が見ている世代。null なら in-flight 無し。 */
  private inFlightSeq: number | null = null;
  /** 単調増加の世代カウンタ。各 pick キックで ++。 */
  private seq = 0;
  /** 最後に最新世代で取れた色 (= 確定色)。begin でリセット。 */
  private lastColor: number | null = null;
  /** commit が pick 未解決時に来たときの保留フラグ。最新世代 resolve で発火。 */
  private commitPending = false;

  constructor(engine: SpuitEngineLike, opts: SpuitCoalescerOptions) {
    this.engine = engine;
    this.opts = opts;
  }

  /**
   * 1 スポイト操作の開始。確定状態をリセット (= この操作で拾った色だけを確定対象に)。
   *
   * 前操作の in-flight pick が遅延 resolve しても無視されるよう、in-flight 世代も
   * 畳む (`inFlightSeq = null`)。worker 往復は順序保証が無いので、前操作の pick が
   * 新操作開始後に resolve → seq ガードで弾く (= 古い世代の遅延 resolve を排除)。
   */
  begin(): void {
    this.lastColor = null;
    this.commitPending = false;
    // 前操作の in-flight 世代を無効化。次の request で新世代を kick する。
    this.inFlightSeq = null;
    this.latest = null;
  }

  /**
   * pick 対象を更新し、in-flight が無ければ pick をキックする。
   * in-flight があれば `latest` の上書きだけ (resolve 時に最新で再 pick される)。
   */
  request(target: SpuitTarget): void {
    this.latest = target;
    if (this.inFlightSeq === null) this.kick();
  }

  /** 操作の release。確定の1色を通知 (取れていれば即、未解決なら pending)。 */
  commit(): void {
    if (this.opts.onCommit === undefined) return;
    if (this.lastColor !== null) {
      this.commitPending = false;
      this.opts.onCommit(this.lastColor);
      return;
    }
    // 未解決: 最新世代の pick が resolve したら commit する。
    this.commitPending = true;
  }

  private kick(): void {
    const target = this.latest;
    if (target === null) return;
    const mySeq = ++this.seq;
    this.inFlightSeq = mySeq;
    void pickColorFromEngine(this.engine, target.mode, target.layerId, target.x, target.y).then(
      (result) => {
        // seq ガード: 自分が現在の in-flight 世代でなければ (= 既に新しい pick が始まって
        // いる、または操作が畳まれた) 結果を捨てる。順不同 resolve の上書きを排除。
        if (this.inFlightSeq !== mySeq) return;
        this.inFlightSeq = null;
        if (result !== null) {
          if (result.kind === 'eraser') {
            // モード B で α0 (= 完全透明)。色は変えずツールを消しゴムへ切り替える
            // (spuit-sample-modes-2026-06-22 §2 B)。確定色 (lastColor) は更新しない
            // (= 透明ピックでは最近色を残さない)。commit が保留されていても発火しない
            // (色が無いので)。
            this.opts.onEraser?.();
          } else {
            this.lastColor = result.rgb24;
            this.opts.onColor(result.rgb24);
            if (this.commitPending) {
              this.commitPending = false;
              this.opts.onCommit?.(result.rgb24);
            }
          }
        }
        // coalescing 再 pick: in-flight 中に request で `latest` が更新されていれば、
        // 最新位置でもう1回だけ撃つ (worker スループットにペース合わせ + 現在位置へ収束)。
        if (this.latest !== target) this.kick();
      },
    );
  }
}
